import { FinancialWorkspacePayrollFiltersDocument } from 'generated/resources/graphql'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { useFinancialWorkspace } from 'modules/finance/components/FinancialWorkspace/Providers/Provider'
import { FilterValue } from 'modules/finance/components/FinancialWorkspace/types/filter'
import { usePermissionsData } from 'modules/finance/components/FinancialWorkspaceEmployeeCosts/Providers/PermissionsProvider'

import { PayrollLineFilter } from './PayrollLine'
import { PayrollPolicyFilter } from './PayrollPolicy'

export const useFilters = () => {
  const { dispatch } = useFinancialWorkspace()
  const { company } = useCurrentSession()
  const {
    permissions: { canSeeCompensations },
  } = usePermissionsData()
  const { data } = useQuery(FinancialWorkspacePayrollFiltersDocument, {
    variables: {
      companyId: company.id,
    },
  })

  const features = data?.features?.featuresConnection?.nodes
    .filter((feature) => feature.enabled)
    .map((feature) => feature.name)

  const hasPolicyFiltersFF = features?.includes('dev_fw_policy_filters') ?? false

  const filters = useUnsafeCallback(
    (recentlyAddedFilter: string | undefined): Record<string, FilterValue> => {
      return {
        payrollLineFilter: {
          component: (
            <PayrollLineFilter
              key="payroll-line-filter"
              defaultOpen={recentlyAddedFilter === 'payrollLineFilter'}
            />
          ),
          label: i18n.t('finance.financial_workspace.payroll.filters.concepts'),
          value: 'payrollLineFilter',
          key: 'conceptIds',
          grouped: false,
          onReset: () => dispatch({ type: 'SET_CONCEPT_IDS' }),
        },
        payrollPolicyFilter: {
          component: (
            <PayrollPolicyFilter
              key="payroll-policy-filter"
              defaultOpen={recentlyAddedFilter === 'payrollPolicyFilter'}
            />
          ),
          label: i18n.t('finance.financial_workspace.payroll.filters.policies'),
          value: 'payrollPolicyFilter',
          key: 'policyIds',
          visible: !canSeeCompensations && hasPolicyFiltersFF,
          onReset: () => dispatch({ type: 'SET_POLICY_IDS' }),
        },
      }
    },
    [dispatch, hasPolicyFiltersFF]
  )

  return { filters }
}
